import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { useGetTunnelMessagesQuery } from 'api/main';
import { useSendSurveyResultsMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { logoutOpen } from 'reducers/user';
import {
    isStatusOk,
    jsonParseSafe,
    parseInvestorLabel,
    setTunnelChangesHandlers,
} from 'utils/helpers';
import { useSubscriptionAlerts } from 'utils/hooks';
import { fundTypes } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelStep, TunnelBox } from '../common';
import { SubmitButton, ConfirmDialog } from 'components';
import { Typography } from '@mui/material';

const SurveyResults = ({ userStep, handleNextStep, tunnelStep }) => {
    const dispatch = useDispatch();
    const alerts = useSubscriptionAlerts();

    const { data: messages } = useGetTunnelMessagesQuery();
    const [sendSurveyResults, { isLoading: investorLoader, isSuccess }] =
        useSendSurveyResultsMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const investorQualification = useMemo(
        () => tunnelSubscription?.investmentCharacteristics?.investorQualification,
        [tunnelSubscription]
    );
    const fixedCriteria = useMemo(
        () => jsonParseSafe(tunnelSubscription?.investmentCharacteristics?.fixedCriteria) || [],
        [tunnelSubscription]
    );
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);

    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;
    const isFCPR = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.FCPR,
        [tunnelSubscription]
    );

    const slpAlerts = useMemo(
        () =>
            !isSLP || !tunnelSubscription || !tunnelSubscription.userTunnelSteps
                ? []
                : alerts.filter(
                      a =>
                          !a.solved &&
                          (a.message === 'user_knowledge.slp.level1.score' ||
                              a.message === 'user_knowledge.slp.level2.score')
                  ),
        [tunnelSubscription, isSLP, alerts]
    );

    const showSLPParagraph = useMemo(
        () =>
            !!slpAlerts.find(a => {
                const info = jsonParseSafe(a.additionalInfo);
                return info?.rating !== 'High';
            }),
        [slpAlerts]
    );

    const investmentFund = tunnelSubscription?.investmentFund;

    const [proCheckOpen, setProCheckOpen] = useState(false);

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            sendSurveyResults({
                callback: afterSubmitCallback,
            });
        },
        [sendSurveyResults]
    );

    const handleQuit = useCallback(() => {
        setProCheckOpen(false);
        dispatch(logoutOpen());
    }, [dispatch]);

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return false;
    }, []);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit]
    );

    useEffect(() => {
        if (isSuccess) {
            handleNextStep(true);
        }
    }, [handleNextStep, isSuccess]);

    if (isFCPR) {
        return (
            <TunnelStep title={tunnelStep?.title || texts.SURVEY_RESULTS}>
                <TunnelBox>
                    {tunnelStep?.description ? (
                        <div dangerouslySetInnerHTML={{ __html: tunnelStep?.description }} />
                    ) : (
                        <Typography gutterBottom>
                            {texts.SURVEY_RESULTS_FCPR_MESSAGE(investmentFund?.name)}
                        </Typography>
                    )}
                </TunnelBox>
                <SubmitButton
                    text={texts.SUIVANT}
                    onClick={!hasStatusOk ? handleSubmit() : handleNextStep}
                    disabled={investorLoader}
                    loading={investorLoader}
                />
            </TunnelStep>
        );
    }

    return (
        <TunnelStep title={tunnelStep?.title || texts.SURVEY_RESULTS}>
            <TunnelBox>
                {tunnelStep?.description ? (
                    <div dangerouslySetInnerHTML={{ __html: tunnelStep?.description }} />
                ) : (
                    <>
                        <Typography gutterBottom>
                            {texts.SURVEY_RESULTS_SUB_LINE_1}
                            {parseInvestorLabel(investorQualification, messages, fixedCriteria)}
                        </Typography>
                        <br></br>
                        <Typography gutterBottom>
                            {isSLP
                                ? texts.SURVEY_RESULTS_SLP_LINE_2(investmentFund?.name)
                                : texts.SURVEY_RESULTS_SUB_LINE_2(investmentFund?.name)}
                        </Typography>
                        {showSLPParagraph && (
                            <React.Fragment>
                                <br></br>
                                <Typography gutterBottom color="error.main">
                                    {texts.SURVEY_RESULTS_SLP_ALERT_DISCLAIMER}
                                </Typography>
                            </React.Fragment>
                        )}
                        <br></br>
                        <Typography gutterBottom>{texts.SURVEY_RESULTS_SUB_LINE_3}</Typography>
                        <ul>
                            <li>
                                <Typography gutterBottom>
                                    {texts.SURVEY_RESULTS_SUB_LINE_3_COND_1}
                                </Typography>
                            </li>
                            <li>
                                <Typography gutterBottom>
                                    {texts.SURVEY_RESULTS_SUB_LINE_3_COND_2}
                                </Typography>
                            </li>
                            <li>
                                <Typography gutterBottom>
                                    {texts.SURVEY_RESULTS_SUB_LINE_3_COND_3}
                                </Typography>
                            </li>
                        </ul>
                    </>
                )}
            </TunnelBox>
            <ConfirmDialog
                open={proCheckOpen}
                confirmText={texts.QUITTER}
                cancelText={texts.GO_BACK}
                title={texts.PROFESSIONAL_FUND}
                handleClose={() => setProCheckOpen(false)}
                handleConfirm={handleQuit}
            >
                {texts.PRO_CHECK_CONTENT}
            </ConfirmDialog>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                disabled={investorLoader}
                loading={investorLoader}
            />
        </TunnelStep>
    );
};

SurveyResults.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
    tunnelStep: PropTypes.object,
};

export default SurveyResults;
